import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon } from "lucide-react";
import { adminApi } from "@/features/admin/api";
import { Input } from "@/components/ui/input";
import { ROUTES } from "@/constants/routes";

const STATUSES = [
    { value: "", label: "All" },
    { value: "placed", label: "Placed" },
    { value: "confirmed", label: "Confirmed" },
    { value: "packing", label: "Packing" },
    { value: "out_for_delivery", label: "Out for delivery" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS: Record<string, string> = {
    placed: "bg-slate-500/10 text-slate-700",
    confirmed: "bg-sky-500/10 text-sky-700",
    packing: "bg-amber-500/10 text-amber-700",
    out_for_delivery: "bg-violet-500/10 text-violet-700",
    delivered: "bg-emerald-500/10 text-emerald-700",
    cancelled: "bg-rose-500/10 text-rose-700",
};

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function AdminOrders() {
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "orders", status, search, page],
        queryFn: () =>
            adminApi.listOrders({
                status: status || undefined,
                search: search || undefined,
                page,
                page_size: pageSize,
            }),
        placeholderData: (prev) => prev,
        refetchInterval: 10000,
    });

    const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

    return (
        <div data-testid="admin-orders-page" className="space-y-5">
            <div>
                <h1 className="text-2xl font-extrabold sm:text-3xl">Orders</h1>
                <p className="text-sm text-muted-foreground">
                    {data ? `${data.total} orders` : "Loading…"} · live updates every 10 s
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                    <button
                        key={s.value || "all"}
                        onClick={() => {
                            setStatus(s.value);
                            setPage(1);
                        }}
                        data-testid={`admin-orders-tab-${s.value || "all"}`}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            status === s.value
                                ? "bg-ink text-white"
                                : "border border-border hover:bg-muted"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="relative max-w-md">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    className="pl-9"
                    placeholder="Search by order number (TSGO-…)"
                    data-testid="admin-orders-search"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                />
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Order</th>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Payment</th>
                            <th className="px-4 py-3">Total</th>
                            <th className="px-4 py-3">Placed</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                                    Loading orders…
                                </td>
                            </tr>
                        ) : data && data.items.length > 0 ? (
                            data.items.map((o) => (
                                <tr key={o.id} data-testid={`admin-order-row-${o.id}`}>
                                    <td className="px-4 py-3">
                                        <Link
                                            to={ROUTES.adminOrderDetailBuild(o.id)}
                                            className="font-semibold hover:underline"
                                        >
                                            {o.number}
                                        </Link>
                                        <p className="text-[11px] text-muted-foreground">
                                            {o.item_count} items
                                        </p>
                                    </td>
                                    <td className="px-4 py-3">
                                        {o.customer ? (
                                            <>
                                                <p className="font-medium">{o.customer.full_name || "—"}</p>
                                                <p className="text-[11px] text-muted-foreground">{o.customer.email}</p>
                                            </>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
                                                STATUS_COLORS[o.status] || "bg-muted"
                                            }`}
                                        >
                                            {o.status.replace(/_/g, " ")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        <span className="font-semibold uppercase">{o.payment.method}</span>
                                        <span
                                            className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                                                o.payment.status === "paid"
                                                    ? "bg-emerald-500/10 text-emerald-700"
                                                    : o.payment.status === "refunded"
                                                      ? "bg-blue-500/10 text-blue-700"
                                                      : o.payment.status === "failed"
                                                        ? "bg-rose-500/10 text-rose-700"
                                                        : "bg-amber-500/10 text-amber-700"
                                            }`}
                                        >
                                            {o.payment.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-semibold">{rupee(o.total)}</td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">
                                        {new Date(o.placed_at).toLocaleString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                                    No orders match.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="rounded-full border border-border px-3 py-1 disabled:opacity-40"
                    >
                        Prev
                    </button>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        className="rounded-full border border-border px-3 py-1 disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}
